interface VisibilityTrackingOptions {
  rootMargin?: string
  threshold?: number | number[]
  throttleDelay?: number
  onVisible?: (items: any[]) => void
  adaptiveThrottling?: boolean
}

interface VisibilityState {
  isVisible: boolean
  firstVisibleTime?: number
  totalVisibleTime: number
  lastVisibleTime?: number
  impressionCount: number
}

export function useVisibilityTracking<T extends { id: string }>(
  options: VisibilityTrackingOptions = {},
) {
  const {
    rootMargin = '128px 0px',
    threshold = 0,
    throttleDelay = 300,
    onVisible,
    adaptiveThrottling = true,
  } = options

  // Core state
  const observer = ref<IntersectionObserver | null>(null)
  const visibilityMap = new Map<string, VisibilityState>()
  const elementToItemMap = new WeakMap<Element, T>()

  // Performance optimization: pre-allocated arrays
  const pendingUpdates: T[] = []
  let updateTimer: NodeJS.Timeout | null = null

  // Performance monitoring
  const performanceMetrics = ref({
    callbackDuration: 0,
    totalCallbacks: 0,
    averageCallbackDuration: 0,
  })

  // Adaptive throttling based on device performance
  const getAdaptiveDelay = () => {
    if (!adaptiveThrottling)
      return throttleDelay

    const cores = navigator.hardwareConcurrency || 4
    const memory = (navigator as any).deviceMemory || 4 // GB

    // Adjust delay based on device capabilities
    if (cores <= 2 || memory <= 2) {
      return throttleDelay * 2 // Low-end device
    }
    else if (cores >= 8 && memory >= 8) {
      return throttleDelay * 0.5 // High-end device
    }
    return throttleDelay
  }

  // Batch process visibility updates
  const processPendingUpdates = () => {
    if (pendingUpdates.length === 0)
      return

    const startTime = performance.now()
    const visibleItems: T[] = []
    const now = Date.now()

    // Process all pending updates
    for (const item of pendingUpdates) {
      const state = visibilityMap.get(item.id)
      if (!state)
        continue

      if (state.isVisible) {
        if (!state.firstVisibleTime) {
          state.firstVisibleTime = now
          state.impressionCount++
        }
        state.lastVisibleTime = now
        visibleItems.push(item)
      }
      else if (state.lastVisibleTime) {
        // Calculate total visible time
        state.totalVisibleTime += now - state.lastVisibleTime
        state.lastVisibleTime = undefined
      }
    }

    // Call the visibility handler if provided
    if (visibleItems.length > 0 && onVisible) {
      onVisible(visibleItems)
    }

    // Clear pending updates efficiently
    pendingUpdates.length = 0

    // Update performance metrics
    const duration = performance.now() - startTime
    performanceMetrics.value.callbackDuration = duration
    performanceMetrics.value.totalCallbacks++
    performanceMetrics.value.averageCallbackDuration
      = (performanceMetrics.value.averageCallbackDuration * (performanceMetrics.value.totalCallbacks - 1) + duration)
        / performanceMetrics.value.totalCallbacks
  }

  // Optimized intersection callback
  const intersectionCallback = (entries: IntersectionObserverEntry[]) => {
    // Use requestAnimationFrame to batch DOM operations
    requestAnimationFrame(() => {
      for (const entry of entries) {
        const item = elementToItemMap.get(entry.target)
        if (!item)
          continue

        let state = visibilityMap.get(item.id)
        if (!state) {
          state = {
            isVisible: false,
            totalVisibleTime: 0,
            impressionCount: 0,
          }
          visibilityMap.set(item.id, state)
        }

        // Only process if visibility actually changed
        if (state.isVisible !== entry.isIntersecting) {
          state.isVisible = entry.isIntersecting

          // Add to pending updates if not already there
          if (!pendingUpdates.includes(item)) {
            pendingUpdates.push(item)
          }
        }
      }

      // Schedule batch processing
      if (pendingUpdates.length > 0) {
        if (updateTimer)
          clearTimeout(updateTimer)
        updateTimer = setTimeout(processPendingUpdates, getAdaptiveDelay())
      }
    })
  }

  // Initialize the observer
  const initialize = () => {
    if (observer.value)
      return

    observer.value = new IntersectionObserver(intersectionCallback, {
      root: null,
      rootMargin,
      threshold,
    })
  }

  // Track an element
  const track = (element: Element, item: T) => {
    if (!observer.value) {
      initialize()
    }

    elementToItemMap.set(element, item)
    observer.value!.observe(element)
  }

  // Untrack an element
  const untrack = (element: Element) => {
    if (!observer.value)
      return

    const item = elementToItemMap.get(element)
    observer.value.unobserve(element)
    elementToItemMap.delete(element)

    // Simply remove from visibility map - let GC handle cleanup
    if (item) {
      visibilityMap.delete(item.id)
    }
  }

  // Track multiple elements at once (more efficient)
  const trackBatch = (elements: Array<{ element: Element, item: T }>) => {
    if (!observer.value) {
      initialize()
    }

    // Batch DOM operations
    requestAnimationFrame(() => {
      for (const { element, item } of elements) {
        elementToItemMap.set(element, item)
        observer.value!.observe(element)
      }
    })
  }

  // Get visibility analytics for an item
  const getVisibilityAnalytics = (itemId: string) => {
    const state = visibilityMap.get(itemId)
    if (!state)
      return null

    const now = Date.now()
    let totalTime = state.totalVisibleTime

    // Add current session time if currently visible
    if (state.isVisible && state.lastVisibleTime) {
      totalTime += now - state.lastVisibleTime
    }

    return {
      totalVisibleTime: totalTime,
      impressionCount: state.impressionCount,
      isCurrentlyVisible: state.isVisible,
      firstVisibleTime: state.firstVisibleTime,
    }
  }

  // Get all visibility analytics
  const getAllVisibilityAnalytics = () => {
    const analytics: Record<string, ReturnType<typeof getVisibilityAnalytics>> = {}

    for (const [id] of visibilityMap) {
      const data = getVisibilityAnalytics(id)
      if (data) {
        analytics[id] = data
      }
    }

    return analytics
  }

  // Cleanup
  const cleanup = () => {
    if (updateTimer) {
      clearTimeout(updateTimer)
      updateTimer = null
    }

    // Process any remaining updates
    processPendingUpdates()

    if (observer.value) {
      observer.value.disconnect()
      observer.value = null
    }

    visibilityMap.clear()
    pendingUpdates.length = 0
  }

  // Auto cleanup on unmount
  onBeforeUnmount(cleanup)

  return {
    track,
    untrack,
    trackBatch,
    getVisibilityAnalytics,
    getAllVisibilityAnalytics,
    performanceMetrics,
    cleanup,
  }
}
